import * as Yup from "yup";
import moment from "moment";
import {
	getFileNameFromUrl,
	getOriginalFileName,
	timezoneChecker,
} from "utils/helpers";
import { defaultOptions as defaultQuoteItems } from "components/Pages/CreateRFQ/QuoteCard/tableDefaultOptions";
import { QUESTION_TYPE_ID } from "newui/features/Rfx/Submission/constants";
import { filterFieldValues } from "newui/features/Rfx/Submission/submission-helpers";
import {
	generateGuestInviteSchema,
	generateQuestionSchema,
	generateQuoteSchema,
} from "newui/features/Rfx/Creation/validation-schemas";

export const getTabLists = (isTemplate, rfxType) => {
	const showQuote = rfxType?.show_quote ?? true;

	const quoteTab = showQuote && {
		value: 4,
		label: "Quote",
		description: "Create/Upload a quoting template",
		title: "QUOTE",
		subTitle:
			"Create or upload a quoting template for the Supplier to complete as part of their response. \nSimply describe the scope and nominate a unit of measurement from the dropdown.",
	};

	const baseTabs = [
		{
			value: 1,
			label: isTemplate ? "Template Details" : "RFx Details",
			description: isTemplate
				? "Provide template details"
				: "Provide project and RFx details",
			title: isTemplate ? "TEMPLATE DETAILS" : "RFx DETAILS",
			subTitle: isTemplate
				? "Please specify the details of the template."
				: "Please specify the details of the RFx and the required scope.",
		},
		{
			value: 2,
			label: "Documentation",
			description: "Upload relevant documents",
			title: "Documentation",
			subTitle:
				"Identify and include relevant project documents such as Project Briefs, Plans and Drawings, Approvals, Terms and Conditions, etc.",
		},
		{
			value: 3,
			label: "Returnable Schedules",
			description: "Create your questionnaire",
			title: "Returnable Schedules",
			subTitle:
				"Here you can create requests or questions and customise how answers can be provided by using the 'CUSTOMISE' dropdown. If you have returnable schedules for the respondents you can upload these as well.",
		},
	];

	if (showQuote) {
		baseTabs.push(quoteTab);
	}

	if (!isTemplate) {
		baseTabs.push({
			value: 5,
			label: "RFx Participants",
			description: "Confirm RFx participants and send",
			title: "RFx Participants",
			subTitle:
				"Select suppliers that will receive the RFQ. You can also invite guest users.",
		});
	}

	return baseTabs.sort((a, b) => a.value - b.value);
};

const defaultSections = [
	{
		id: crypto.randomUUID(),
		name: "",
		description: "",
		sort_order: 1,
		isRemove: 0,
		questionnaire: [
			{
				id: crypto.randomUUID(),
				question: "",
				type: QUESTION_TYPE_ID.PARAGRAPH,
				required: 1,
				fields: [""],
				name: "",
				sort_order: 1,
			},
		],
	},
];

const defaultQuoteStructure = [
	{
		name: "",
		description: "",
		items: defaultQuoteItems(),
		sort_order: 1,
		required: 1,
		isRemove: 0,
	},
];

const defaultDeadlineTime = "16:00";

// formValuesGenerator: Determines initial/default values based on template flag
export const formValuesGenerator = (rfqData, isTemplate) => {
	// --- Handle NEW RFX / Template Creation ---
	if (!rfqData) {
		const baseInitialValues = {
			documentation_description: "",
			documents: [],
			returnable_schedule_type: "form",
			sections: defaultSections,
			returnable_schedule_file: "",
			returnable_schedule_file_path: "",
			returnable_schedule_file_id: null,
			quote: defaultQuoteStructure,
			quote_type: "form",
			quote_file: "",
			quote_file_path: "",
			quoteInstructions: "",
			allow_flexible_quote: "0",
			deadline_time: defaultDeadlineTime,
		};

		if (isTemplate) {
			return {
				template_name: "",
				...baseInitialValues,
			};
		} else {
			// Initial values for a new RFX
			return {
				name: "",
				rfq_id_number: "",
				type: "",
				address: {
					latitude: "",
					longitude: "",
					value: "",
				},
				rfx_lead_id: "",
				description: "",
				client: "",
				deadline_date: "",
				deadline_time: defaultDeadlineTime,
				submission_deadline_timezone: "AWST",
				escope_of_service: "",
				suppliers: [],
				guest: [],
				...baseInitialValues,
			};
		}
	}

	// --- Handle EXISTING RFX / Template Data ---
	const effectiveData = rfqData.data || rfqData;

	const documents = effectiveData?.documentationDocument?.map((doc) => ({
		id: doc.id || null,
		document: doc.fileName || null,
		category_id: doc.categoryId || null,
		sort_order: doc.sort_order || null,
		file_size: doc?.fileSize || null,
		path: doc.documentPath || null,
		isRemove: 0,
	}));

	const sections = effectiveData?.questionnaire?.map((section, i) => ({
		id: section.id || crypto.randomUUID(),
		name: section.name || "",
		description: section.description || "",
		sort_order: section.sort_order ? parseInt(section.sort_order) : i + 1,
		isRemove: 0,
		questionnaire: section.questionnaire?.map((question, j) => {
			let fields;
			if (question.type === QUESTION_TYPE_ID.CORPORATE_DOCUMENTS) {
				fields = question.fields;
			} else {
				fields = filterFieldValues(question) || [];
			}

			return {
				id: question.id || crypto.randomUUID(),
				name: question.name || "",
				fields: fields,
				sort_order: j + 1,
				required: question.required ? 1 : 0,
				type: question.type ? parseInt(question.type) : null,
				isRemove: 0,
				hasComment: question.hasComment,
			};
		}),
	}));

	const quotes = effectiveData?.quotes?.map((quote) => ({
		id: quote.id || crypto.randomUUID(),
		name: quote.name || "",
		description: quote.description || "",
		sort_order: quote.sort_order || null,
		required: 1,
		isRemove: 0,
		items: quote?.items?.map((item, k) => ({
			id: item.id || null,
			name: item.name || "",
			unit_id: item.unit?.id ? item.unit?.id : item?.unit_id,
			sort_order: k + 1,
			placeholder: item.placeholder || "",
			isRemove: item?.isRemove || 0,
		})),
	}));

	const suppliers = effectiveData?.supplierInvited?.all
		?.filter((supplier) => supplier.company)
		.map((supplier) => supplier?.company.id);
	const guest = effectiveData?.supplierInvited?.all
		?.filter((supplier) => !supplier.company)
		.map((supplier) => ({ name: supplier.name, email: supplier.email }));

	// Determine the actual quote file path URL string
	let actualQuoteUrl = "";
	if (typeof effectiveData?.quoteFile?.file_path === "string") {
		actualQuoteUrl = effectiveData.quoteFile?.file_path;
	} else if (
		typeof effectiveData?.quoteFile === "object" &&
		effectiveData?.quoteFile?.file
	) {
		// Handle cases - might be an object like { id: ..., file: 'url' }
		actualQuoteUrl = effectiveData.quoteFile.file;
	}

	const quoteType = effectiveData?.quoteType || "form";
	const processedQuotes = quotes || [];

	const finalQuotes =
		quoteType === "form" && processedQuotes.length === 0
			? defaultQuoteStructure
			: processedQuotes;

	const finalSections =
		sections && sections.length > 0 ? sections : defaultSections;

	const returnableScheduleType =
		effectiveData?.returnableScheduleType || "form";
	const returnableScheduleFileInfo = effectiveData?.returnableScheduleFile;

	let returnableScheduleFileId = null;
	let returnableScheduleFileName = "";
	let returnableScheduleFilePath = "";

	if (returnableScheduleType === "file") {
		if (isTemplate) {
			returnableScheduleFileId = returnableScheduleFileInfo?.id || null;
			returnableScheduleFileName = returnableScheduleFileInfo?.file_name || "";
			returnableScheduleFilePath = returnableScheduleFileInfo?.file_path;
		} else {
			returnableScheduleFileId = null;
			returnableScheduleFileName = returnableScheduleFileInfo || "";
			returnableScheduleFilePath = returnableScheduleFileInfo?.file_path || "";
		}
	}

	// Extract original filename from quote file path for display
	let displayQuoteFileName = "";
	let quoteFileSize = null;
	if (actualQuoteUrl && quoteType === "file") {
		const serverFileName = getFileNameFromUrl(actualQuoteUrl);
		displayQuoteFileName = getOriginalFileName(serverFileName);
		// Get file size from server response
		quoteFileSize = effectiveData?.quoteFile?.file_size || null;
	}

	// Common values for both templates and RFQs
	const commonValues = {
		documentation_description: effectiveData?.documentationDescription || "",
		documents: documents || [],
		returnable_schedule_type: returnableScheduleType,
		sections: finalSections,
		returnable_schedule_file_id: returnableScheduleFileId,
		returnable_schedule_file: returnableScheduleFileName,
		returnable_schedule_file_path: returnableScheduleFilePath,
		quote: finalQuotes,
		quote_type: quoteType,
		quote_file: displayQuoteFileName || "",
		quote_file_path: actualQuoteUrl,
		quoteInstructions: effectiveData?.quoteInstructions,
		quote_file_size: quoteFileSize,
		allow_flexible_quote: effectiveData?.allow_flexible_quote ? "1" : "0",
	};

	if (isTemplate) {
		let templateQuoteFileId = null;
		let templateQuoteFileName = commonValues.quote_file;
		let templateQuoteFilePath = commonValues.quote_file_path;

		if (commonValues.quote_type === "file") {
			templateQuoteFileId = effectiveData?.quoteFile?.id || null;

			if (effectiveData?.quoteFile?.file_name) {
				templateQuoteFileName = getOriginalFileName(
					effectiveData.quoteFile.file_name
				);
			}
		}

		return {
			template_name: rfqData?.name || effectiveData?.name || "",
			...commonValues,
			quote_file_id: templateQuoteFileId,
			quote_file: {
				file_name: templateQuoteFileName,
				file_path: templateQuoteFilePath,
				id: templateQuoteFileId,
			},
			quote_file_path: templateQuoteFilePath,
			returnable_schedule_file_path: commonValues.returnable_schedule_file_path,
		};
	} else {
		return {
			name: effectiveData?.name || "",
			rfq_id_number: effectiveData?.rfqIdNumber || "",
			type: effectiveData?.type || "",
			address: {
				latitude: effectiveData?.locationLatitude || "",
				longitude: effectiveData?.locationLongitude || "",
				value: effectiveData?.location || "",
			},
			rfx_lead_id: effectiveData?.rfxLead?.id || "",
			description: effectiveData?.description || "",
			client: effectiveData?.client || "",
			deadline_date: effectiveData?.submissionDeadline
				? moment(effectiveData?.submissionDeadline)
				: "",
			deadline_time: effectiveData?.submissionDeadline
				? moment(effectiveData?.submissionDeadline).format("HH:mm")
				: defaultDeadlineTime,
			submission_deadline_timezone: effectiveData?.submissionTimezone || "AWST",
			escope_of_service: effectiveData?.escopeOfService || "",
			suppliers: suppliers || [],
			guest: guest || [],
			...commonValues,
			quote_file_id: null, // Explicitly clear quote ID for RFX
			returnable_schedule_file_id: null, // Keep this override for returnable schedules
		};
	}
};

const generateSectionSchema = () => {
	return Yup.object().shape({
		id: Yup.string().nullable(),
		name: Yup.string().when("isRemove", {
			is: (val) => val === 0 || val === null || val === undefined,
			then: Yup.string().required("Section name is required"),
			otherwise: Yup.string().nullable(),
		}),
		description: Yup.string().when("isRemove", {
			is: (val) => val === 0 || val === null || val === undefined,
			then: Yup.string().required("Description is required"),
			otherwise: Yup.string().nullable(),
		}),
		sort_order: Yup.number().nullable(),
		questionnaire: Yup.array().when("isRemove", {
			is: (val) => val === 0 || val === null || val === undefined,
			then: Yup.array()
				.of(generateQuestionSchema())
				.min(1, "Please add at least 1 question for this section"),
			otherwise: Yup.array().nullable(),
		}),
	});
};

const testDeadlineTime = (context, fullError = true) => {
	const { deadline_date, deadline_time, submission_deadline_timezone } =
		context.parent;
	if (!deadline_date || !deadline_time || !submission_deadline_timezone) {
		return true; // skip if missing (required will catch)
	}

	const timezone = timezoneChecker(submission_deadline_timezone);

	// create timezone specific dateTime
	const deadline = moment.tz(
		`${moment(deadline_date).format("YYYY-MM-DD")} ${deadline_time}`,
		"YYYY-MM-DD HH:mm",
		timezone
	);

	// Check if at least 1 hour from now
	const now = moment.tz(timezone);
	const diffHours = deadline.diff(now, "hours", true);

	if (diffHours < 1) {
		// Include current time in the error message
		return context.createError({
			message: fullError
				? `Submission Deadline must be at least 1 hour from now. Current date/time: ${now.format(
						"DD MMM YYYY HH:mm"
					)} (${timezone})`
				: " ",
		});
	}

	return true;
};

export const validationSchema = (isTemplate, rfxType) => {
	// check tabs
	const showQuote = (rfxType?.show_quote ?? isTemplate) ? true : false;

	// Define validation rules for each step
	const stepValidations = {
		// Step 1: RFx Details
		details: {
			// For templates, we only need to validate the template_name
			// For RFQs, we need to validate all fields
			...(isTemplate
				? {
						template_name: Yup.string().required("Template name is required"),
					}
				: {
						name: Yup.string().required("Request Title is required"),
						type: Yup.string().nullable().required("Type is required"),
						client: Yup.string().required("Client is required"),
						rfx_lead_id: Yup.number().required("RFx lead is required"),
						deadline_date: Yup.date()
							.required("Deadline date is required")
							.test(
								"deadline-min-1h",
								"Submission Deadline must be at least 1 hour from now",
								function () {
									return testDeadlineTime(this);
								}
							),
						deadline_time: Yup.string()
							.required("Time is required")
							.test("deadline-min-1h", " ", function () {
								return testDeadlineTime(this, false);
							}),
						address: Yup.object().shape({
							value: Yup.string().required("Location is required"),
						}),
					}),
		},

		// Step 2: Documentation - can be empty for both templates and RFQs
		documentation: {
			documents: Yup.array().nullable(),
			documentation_description: Yup.string().test(
				"len",
				"Input has exceeded the max character limit",
				(val) => (val ? val?.replace(/<\/?[^>]+>/gi, "").length <= 1500 : true)
			),
		},

		// Step 3: Returnable Schedules - Same validation for both templates and RFQs
		schedules: {
			sections: Yup.array().when("returnable_schedule_type", (type, schema) =>
				type !== "file"
					? schema.of(generateSectionSchema()).min(1, "Section is required")
					: schema.nullable()
			),
			returnable_schedule_file: Yup.mixed().when("returnable_schedule_type", {
				is: "file",
				then: Yup.mixed().required(),
			}),
		},

		// Step 4: Quote - Same validation for both templates and RFQs
		quote: showQuote && {
			quote: Yup.array().when("quote_type", (type, schema) =>
				type !== "file"
					? schema.of(generateQuoteSchema()).min(1, "Quote is required")
					: schema.nullable()
			),
			quote_file: Yup.mixed().when("quote_type", {
				is: "file",
				then: Yup.mixed().required("Template file is required"),
			}),
			quoteInstructions: Yup.string()
				.nullable()
				.test("len", "Input has exceeded the max character limit", (val) =>
					val ? val?.replace(/<\/?[^>]+>/gi, "").length <= 3000 : true
				),
			allow_flexible_quote: Yup.string().oneOf(["0", "1"]).required(),
		},

		// Step 5: RFx Participants - Only for RFQs, not for templates
		participants: generateGuestInviteSchema(),
	};

	// Combine all validation rules into one schema
	return Yup.object().shape({
		...stepValidations.details,
		...stepValidations.documentation,
		...stepValidations.schedules,
		...stepValidations.quote,
		...(isTemplate ? {} : stepValidations.participants),
	});
};

export const validStepChecker = (step, errors, isTemplate) => {
	if (!errors) {
		return { isValid: true, errorMessage: "" };
	}

	if (step === 1) {
		return {
			isValid: isTemplate
				? !errors.template_name
				: !errors.type &&
					!errors.client &&
					!errors.deadline_date &&
					!errors.name,
			errorMessage: isTemplate
				? "Please enter a template name"
				: "Please fill all required fields",
		};
	}
	if (step === 2) {
		return {
			isValid: !errors.documents,
			errorMessage: "Please upload at least one document",
		};
	}
	if (step === 3) {
		// Check for section errors
		const sectionErrors = Object.keys(errors).some(
			(key) =>
				key.includes("sections") || key.includes("returnable_schedule_file")
		);

		return {
			isValid: !sectionErrors,
			errorMessage: "Please fill all required fields",
		};
	}
	if (step === 4) {
		const quoteErrors = Object.keys(errors).some((key) =>
			key.includes("quote")
		);
		return {
			isValid: !quoteErrors,
			errorMessage: "Please fill all quote fields",
		};
	}
	if (step === 5 && !isTemplate) {
		return {
			isValid: !errors.guest,
			errorMessage: "Please fill all required fields",
		};
	}
};

export const validateReturnableSchedulesStep = (currentFormValues) => {
	if (currentFormValues?.returnable_schedule_type === "form") {
		if (currentFormValues?.sections?.length > 0) {
			if (
				currentFormValues?.sections?.length === 1 &&
				currentFormValues?.sections[0]?.name === "deleted"
			) {
				return {
					message: "At least one section with questionnaire is required",
					title: "Section Required",
				};
			} else {
				const nullQuestionnaire = isReturnableQuestionnaireNull(
					currentFormValues?.sections
				);
				const nullSection = currentFormValues?.sections?.find(
					(currentSection) => currentSection.name === ""
				);

				if (nullSection)
					return {
						message: "Please enter the value of section",
						title: "Section value is required",
						data: nullSection,
					};

				if (nullQuestionnaire)
					return {
						message: "Please enter the value of questionnaire",
						title: "Question value is required",
						data: nullQuestionnaire,
					};
			}
		}
	} else {
		if (
			!currentFormValues?.returnable_schedule_type ||
			currentFormValues?.returnable_schedule_type?.length < 1
		) {
			return {
				message: "Please upload returnables schedules templates",
				title: "Returnable schedules template is required",
				data: "returnable_schedule_type",
			};
		}
	}
};

const isReturnableQuestionnaireNull = (currentFormSection) => {
	return currentFormSection?.find((currentSection) => {
		return currentSection?.questionnaire?.find(
			(question) => question?.name === ""
		);
	});
};

export const isQuoteFormInvalid = (quoteArray) => {
	if (!quoteArray || !Array.isArray(quoteArray)) {
		return true;
	}
	// Check each non-removed quote section
	for (const section of quoteArray) {
		if (!section.isRemove) {
			if (
				!section.name ||
				section?.name?.length === 0 ||
				!section.description ||
				section?.description?.length === 0
			) {
				return true;
			}
			// Check each non-removed item within the section
			if (!section.items || !Array.isArray(section.items)) return true;
			for (const item of section.items) {
				if (!item.isRemove) {
					// Check required item fields
					if (!item.name || !item.unit_id) {
						return true;
					}
				}
			}
		}
	}
	return false;
};
